import { type CacheAnalyticsCallback, promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise, type DegradationCallback } from '../../utils/degradable-promise';

import type { OutboundAuthContainerResponse } from './types';

const outboundAuthCache = promiseCache<OutboundAuthContainerResponse>({
	name: 'outbound-auth-cache',
	expiration: 1000 * 60 * 5, // 5 minutes
});

export type OutboundAuthFetchProps = {
	/**
	 * Atlassian Account ID
	 */
	aaid: string;

	/**
	 * Container ID (UUID)
	 */
	containerId: string;

	/**
	 * Service key to filter by (e.g., 'github')
	 */
	serviceKey: string;

	/**
	 * Determines if cached value should be refreshed if previous value has not expired yet
	 */
	onlyRefreshIfExpired?: boolean;

	/**
	 * Duration (in ms) that the result of this request should be cached for
	 */
	expiration?: number;

	/**
	 * Callback to execute when API is too slow and must degrade
	 */
	degradationCallback?: DegradationCallback;

	/**
	 * Callback to execute with outcome of cache operation for analytics
	 */
	cacheAnalyticsCallback?: CacheAnalyticsCallback;

	/**
	 * If true, the cached value will be returned even if it has expired
	 */
	returnExpired?: boolean;
};

/**
 * AaidcontainerbyaaidandcontaineridGet
 * https://bitbucket.org/atlassian/outbound-auth/src/10269590a6d61b051f11104231a3159e4a5fc49a/docs/content/platform/outbound-auth/swagger.yaml?at=master#lines-1392
 * @param aaid - Atlassian Account ID
 * @param containerId - Container UUID
 * @param serviceKey - Optional service key to filter by
 * @returns Promise<OutboundAuthContainerResponse> - The API response
 */
export const fetchOutboundAuthDataInternal = async (
	aaid: string,
	containerId: string,
	serviceKey?: string,
): Promise<OutboundAuthContainerResponse> => {
	const queryParams = new URLSearchParams();
	if (serviceKey) {
		queryParams.append('serviceKey', serviceKey);
		// include=aaId - This bypasses token refresh and profile fetching operations. Adding include=accessToken can trigger token refresh from external providers, and omitting this include query parameter would result in displayName and avatarUrl being added by default to includeFields, which can trigger external API calls for profile data.
		queryParams.append('include', 'aaid');
	}

	const queryString = queryParams.toString();
	const url = `/gateway/api/outbound-auth/aaId/${aaid}/container/${containerId}${queryString ? `?${queryString}` : ''}`;

	const response = await fetch(url, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	});

	if (!response.ok) {
		throw new Error('Failed to fetch outbound auth data');
	}

	return (await response.json()) as OutboundAuthContainerResponse;
};

/**
 * Fetches outbound auth data for a given user and container with caching
 * @param props - Configuration object with aaid, containerId, serviceKey and optional callbacks
 * @returns Promise<OutboundAuthContainerResponse> - The API response
 */
export const fetchOutboundAuthData = async ({
	aaid,
	containerId,
	serviceKey,
	onlyRefreshIfExpired = true,
	expiration,
	degradationCallback = () => {},
	cacheAnalyticsCallback,
	returnExpired = false,
}: OutboundAuthFetchProps): Promise<OutboundAuthContainerResponse> => {
	const supplier: () => Promise<OutboundAuthContainerResponse> = () => {
		const outboundAuthPromise = fetchOutboundAuthDataInternal(aaid, containerId, serviceKey);

		const degrader = new DegradablePromise<OutboundAuthContainerResponse>(
			outboundAuthPromise,
			degradationCallback,
		);

		return degrader.promise();
	};

	const cacheKey = `${aaid}-${containerId}-${serviceKey}`;

	return outboundAuthCache.refreshOnAccessDecorator({
		key: cacheKey,
		supplier,
		returnExpired,
		onlyRefreshIfExpired,
		expiration,
		analyticsCallback: cacheAnalyticsCallback,
	});
};
